import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "motion/react";
import { useWide } from "../hooks/useWide";
import { fmtCode, padBox, partFrames } from "../data/format";
import { ChevronLeft, ChevronRight, Download, Loader2, X, ZoomIn, ZoomOut } from "lucide-react";

/**
 * Tasvirni katta ko'rish: qismlar (rasm qotib turadi, ro'yxat suriladi)
 * va ishlab chiqarish kadrlari. Koordinatalar API dagi kabi 0-1000,
 * `image.jpg` ga nisbatan: box = [ymin, xmin, ymax, xmax], point = [y, x].
 */

const GROUP_COLOR = {
  metall: "#5b6b82",
  plastmassa: "#e8833a",
  elektronika: "#7b4fd6",
  rezina: "#3c3c3c",
  shisha_keramika: "#2ba3c4",
  toqimachilik: "#d64f8a",
  yogoch_qogoz: "#a0703c",
  kimyoviy: "#2f80ed",
  boshqa: "#8c959f",
};

const colorOf = (p) => GROUP_COLOR[p.material_group] ?? GROUP_COLOR.boshqa;

// Rasmning yuqori qismi (qismlarga ajratilgan ko'rinish) — jarayon kadrlaridan yuqorisi
const cropTop = (view) =>
  Math.min(...(view.process ?? []).map((p) => p.box?.[0] ?? 1000), 1000);

// Elementning joriy o'lchami [kenglik, balandlik]
function useBox() {
  const [box, setBox] = useState(null);
  const ro = useRef(null);
  const ref = useCallback((el) => {
    ro.current?.disconnect();
    if (!el) return;
    ro.current = new ResizeObserver(([e]) => setBox([e.contentRect.width, e.contentRect.height]));
    ro.current.observe(el);
  }, []);
  return [ref, box];
}

// [kenglik, balandlik] ni qutiga sig'diradi; `max` — kadr piksellaridan necha barobar katta
function useFit(w, h, max = Infinity) {
  const [ref, box] = useBox();
  if (!box || !w || !h) return [ref, null];
  const k = Math.min(box[0] / w, box[1] / h, max);
  return [ref, { width: Math.floor(w * k), height: Math.floor(h * k) }];
}

// Telefonda "orqaga" sahifadan chiqib ketmasin — oynani yopsin
function useBackClose(onClose) {
  useEffect(() => {
    history.pushState({ overlay: true }, "");
    window.addEventListener("popstate", onClose);
    return () => window.removeEventListener("popstate", onClose);
  }, [onClose]);
  return useCallback(() => history.back(), []);
}

const GAP = 10; // raqam doirasi bilan obyekt cheti orasidagi bo'shliq, px
const PAD = 0.025; // obyekt atrofida qoldiriladigan oq maydon (rasm ulushi)

// Oq fon ustidagi obyekt chegarasi (rasm ulushida, `top` gacha qatorlar).
// Rasmlarda obyekt atrofi keng oq maydon — u kesilsa telefonda obyekt
// 2-3 barobar katta ko'rinadi. Fon oq bo'lmasa chegara = butun rasm.
const BOUNDS = new Map();
function contentBounds(img, top) {
  try {
    const W = 400;
    const H = Math.round((W * img.naturalHeight) / img.naturalWidth);
    const rows = Math.max(1, Math.round(H * top));
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = rows;
    const g = canvas.getContext("2d", { willReadFrequently: true });
    g.drawImage(img, 0, 0, W, H);
    const d = g.getImageData(0, 0, W, rows).data;
    let [l, r, t, b] = [W, -1, rows, -1];
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < W; x++) {
        const i = (y * W + x) * 4;
        // 238: JPEG shovqini (~250) fon, yumshoq soya (~230) obyekt hisoblanadi
        if (d[i] * 0.3 + d[i + 1] * 0.59 + d[i + 2] * 0.11 < 238) {
          if (x < l) l = x;
          if (x > r) r = x;
          if (y < t) t = y;
          if (y > b) b = y;
        }
      }
    }
    return r < 0 ? null : { l: l / W, r: (r + 1) / W, t: t / H, b: (b + 1) / H };
  } catch {
    return null; // boshqa domendagi rasm (CORS) — kesilmaydi
  }
}

function useBounds(src, top) {
  const key = `${src}|${top}`;
  const [, rerender] = useState(0);
  useEffect(() => {
    if (BOUNDS.has(key)) return;
    const img = new Image();
    img.onload = () => {
      BOUNDS.set(key, contentBounds(img, top));
      rerender((n) => n + 1);
    };
    img.onerror = () => {
      BOUNDS.set(key, null);
      rerender((n) => n + 1);
    };
    img.src = src;
    return () => (img.onload = img.onerror = null);
  }, [key, src, top]);
  return BOUNDS.has(key) ? BOUNDS.get(key) : undefined;
}

/**
 * Raqamlar posterdagi kabi (compose.py `_callouts`): obyektning chap va
 * o'ng yonida ustun bo'lib, detaldagi kichik nuqtaga chiziq bilan.
 * Raqamni detal ustiga qo'yish mayda detallarni yopib qo'yardi.
 *
 * Ustun obyekt chetiga (qismlar qutilari birlashmasiga) yopishadi; rasmda
 * bo'sh joy yetmasa yon tomonga oq hoshiya qo'shiladi. Hammasi px da:
 * doira o'lchami ekran o'lchamiga bog'liq emas.
 */
function layout(view, size, bounds, avail, maxH, D, stretch) {
  const R = D / 2;
  const top = cropTop(view) / 1000;
  const pts = (view.parts ?? [])
    .filter((p) => p.point || p.box)
    .map((p) => {
      const [y, x] = p.point ?? [(p.box[0] + p.box[2]) / 2, (p.box[1] + p.box[3]) / 2];
      return { part: p, y: y / 1000, x: x / 1000 };
    })
    .filter((q) => q.y <= top);

  // Obyekt: piksel chegarasi va qism qutilari birlashmasi
  const xs = pts.flatMap((q) => (q.part.box ? [q.part.box[1] / 1000, q.part.box[3] / 1000] : [q.x]));
  const ys = pts.flatMap((q) => (q.part.box ? [q.part.box[0] / 1000, q.part.box[2] / 1000] : [q.y]));
  if (bounds) {
    xs.push(bounds.l, bounds.r);
    ys.push(bounds.t, bounds.b);
  }
  const [oL, oR] = xs.length ? [Math.min(...xs), Math.max(...xs)] : [0, 1];
  const [oT, oB] = ys.length ? [Math.min(...ys), Math.max(...ys)] : [0, top];
  const cL = Math.max(0, oL - PAD);
  const cR = Math.min(1, oR + PAD);
  const cT = Math.max(0, oT - PAD);
  const cB = Math.min(top, oB + PAD);
  const ratio = ((cB - cT) * size.h) / ((cR - cL) * size.w);
  pts.forEach((q) => {
    q.x = (q.x - cL) / (cR - cL);
    q.y = (q.y - cT) / (cB - cT);
  });
  const extL = (oL - cL) / (cR - cL);
  const extR = (oR - cL) / (cR - cL);
  const need = D + GAP;
  const guts = (w) => [Math.max(0, need - extL * w), Math.max(0, need - (1 - extR) * w)];
  let w = Math.min(avail, maxH / ratio);
  for (let i = 0; i < 8; i++) {
    const [a, b] = guts(w);
    const over = w + a + b - avail;
    if (over < 0.5) break;
    w -= over;
  }
  let [gl, gr] = guts(w);
  // Kartada oq maydon to'liq enni egallaydi, rasm o'rtada
  const extra = stretch ? Math.max(0, avail - (w + gl + gr)) / 2 : 0;
  gl += extra;
  gr += extra;
  const imgH = w * ratio;

  // Tomon: obyekt o'rtasiga nisbatan; bir tomonda 2 tadan ortiq ko'p bo'lsa o'rtaga eng yaqini o'tadi
  const mid = (extL + extR) / 2;
  pts.forEach((q) => (q.left = q.x < mid));
  for (;;) {
    const ls = pts.filter((q) => q.left);
    const rs = pts.filter((q) => !q.left);
    if (Math.abs(ls.length - rs.length) <= 2) break;
    const heavy = ls.length > rs.length ? ls : rs;
    heavy.reduce((a, b) => (Math.abs(a.x - mid) < Math.abs(b.x - mid) ? a : b)).left = ls.length <= rs.length;
  }

  const step = D + 4;
  const most = Math.max(pts.filter((q) => q.left).length, pts.filter((q) => !q.left).length);
  const H = Math.max(imgH, most * step + 4);
  const offY = (H - imgH) / 2;
  const colX = { true: gl + extL * w - GAP - R, false: gl + extR * w + GAP + R };
  for (const left of [true, false]) {
    const side = pts.filter((q) => q.left === left).sort((a, b) => a.y - b.y);
    let prev = R + 2 - step;
    for (const q of side) {
      q.ax = gl + q.x * w;
      q.ay = offY + q.y * imgH;
      q.cx = colX[left];
      q.cy = Math.max(q.ay, prev + step);
      prev = q.cy;
    }
    const over = prev - (H - R - 2);
    if (over > 0) side.forEach((q) => (q.cy -= over));
  }
  const fullW = w / (cR - cL);
  const fullH = (fullW * size.h) / size.w;
  return {
    // Pastga yaxlitlanadi: yarim piksel ortiqcha ham konteynerni kengaytirib, o'lchash
    // halqasiga olib kelardi (katta ko'rinishda rasm sekin o'sib, ro'yxatni siqardi)
    width: Math.floor(gl + w + gr),
    height: Math.floor(H),
    R,
    items: pts,
    img: { left: gl, top: offY, w, h: imgH, fullW, fullH, dx: -cL * fullW, dy: -cT * fullH },
  };
}

/** Qismlarga ajratilgan rasm va raqamlar. `fill` — ota qutini to'ldiradi (katta ko'rinish). */
export function Exploded({ view, size, active, onPick, onHover, fill = false, maxH, src, className = "" }) {
  const image = src ?? view.files.image;
  const [ref, box] = useBox();
  const top = cropTop(view);
  const bounds = useBounds(image, top / 1000);
  const D = box && box[0] < 500 ? 22 : 28;
  const L =
    box && bounds !== undefined &&
    layout(view, size, bounds, box[0], fill ? box[1] : (maxH ?? window.innerHeight * 0.7), D, !fill);
  const [under, setUnder] = useState(null);

  // Kursor ostidagi qism; qutilar ustma-ust bo'lsa eng kichigi (korpus qutisi ichidagi detal)
  const partAt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - r.left - L.img.dx) / L.img.fullW) * 1000;
    const y = ((e.clientY - r.top - L.img.dy) / L.img.fullH) * 1000;
    let best = null;
    let area = Infinity;
    for (const { part: p } of L.items) {
      const b = p.box;
      if (!b || y < b[0] || y > b[2] || x < b[1] || x > b[3]) continue;
      const a = (b[2] - b[0]) * (b[3] - b[1]);
      if (a < area) [best, area] = [p.n, a];
    }
    return best;
  };
  const point = (n) => {
    if (n === under) return;
    setUnder(n);
    onHover?.(n);
  };

  return (
    <div ref={ref} className={fill ? "flex h-full w-full min-w-0 items-center justify-center" : "w-full min-w-0"}>
      {!L && (
        <div
          className={`skeleton ${fill ? "h-full max-w-full" : "w-full"} ${className}`}
          style={{ aspectRatio: `${size.w} / ${(size.h * top) / 1000}` }}
        />
      )}
      {L && (
        <motion.div
          initial={{ opacity: 0, scale: 0.985 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className={`relative mx-auto overflow-hidden bg-white ${className}`}
          style={{ width: L.width, height: L.height }}
        >
          <div
            className="absolute overflow-hidden"
            style={{ left: L.img.left, top: L.img.top, width: L.img.w, height: L.img.h, cursor: under ? "pointer" : undefined }}
            onClick={(e) => {
              const n = partAt(e);
              if (!n) return;
              e.stopPropagation();
              onPick?.(n);
            }}
            onMouseMove={(e) => point(partAt(e))}
            onMouseLeave={() => point(null)}
          >
            <img
              src={image}
              alt={view.product?.name}
              draggable={false}
              className="absolute block max-w-none"
              style={{ left: L.img.dx, top: L.img.dy, width: L.img.fullW, height: L.img.fullH }}
            />
            {L.items.filter((q) => q.part.n === active && q.part.box).map(({ part: p }) => (
              <div
                key={p.n}
                className="pointer-events-none absolute rounded-md border-2"
                style={{
                  borderColor: colorOf(p),
                  background: `${colorOf(p)}1f`,
                  left: L.img.dx + (p.box[1] / 1000) * L.img.fullW,
                  top: L.img.dy + (p.box[0] / 1000) * L.img.fullH,
                  width: ((p.box[3] - p.box[1]) / 1000) * L.img.fullW,
                  height: ((p.box[2] - p.box[0]) / 1000) * L.img.fullH,
                }}
              />
            ))}
          </div>

          <svg className="pointer-events-none absolute inset-0" width={L.width} height={L.height}>
            {L.items.map((q) => {
              const on = active === q.part.n;
              const x1 = q.cx + (q.left ? L.R : -L.R);
              return (
                <g key={q.part.n} opacity={active && !on ? 0.3 : 1}>
                  <line x1={x1} y1={q.cy} x2={q.ax} y2={q.ay} stroke="white" strokeWidth={4} strokeLinecap="round" />
                  <line
                    x1={x1}
                    y1={q.cy}
                    x2={q.ax}
                    y2={q.ay}
                    stroke={on ? colorOf(q.part) : "#334155"}
                    strokeWidth={on ? 2.5 : 1.5}
                    strokeLinecap="round"
                  />
                  <circle cx={q.ax} cy={q.ay} r={on ? 5.5 : 4} fill={colorOf(q.part)} stroke="white" strokeWidth={2} />
                </g>
              );
            })}
          </svg>

          {L.items.map(({ part: p, cx, cy }) => {
            const on = active === p.n;
            return (
              <button
                key={p.n}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onPick?.(p.n);
                }}
                onMouseEnter={() => onHover?.(p.n)}
                onMouseLeave={() => onHover?.(null)}
                aria-label={p.name}
                className={`absolute flex items-center justify-center rounded-full bg-white font-bold text-slate-900 shadow-md transition-transform ${
                  D < 28 ? "border-2 text-[11px]" : "border-[3px] text-[13px]"
                }`}
                style={{
                  left: cx - L.R,
                  top: cy - L.R,
                  width: D,
                  height: D,
                  borderColor: colorOf(p),
                  zIndex: on ? 2 : 1,
                  transform: `scale(${on ? 1.2 : 1})`,
                }}
              >
                {p.n}
              </button>
            );
          })}
        </motion.div>
      )}
    </div>
  );
}

/** Ro'yxatdagi bitta qism. */
/** Qismning o'zi — umumiy rasmdan qutisi bo'yicha kesib; burchakda raqami (rasmdagi belgi bilan bog'liq). */
function PartThumb({ view, size, src, part: p, onZoom }) {
  const b = padBox(p.box);
  const w = (b[3] - b[1]) * size.w;
  const h = (b[2] - b[0]) * size.h;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onZoom?.(p.n);
      }}
      title="Qismni kattalashtirish"
      className="relative flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white ring-sky-400/70 transition hover:ring-2 sm:h-16 sm:w-16"
    >
      <Crop view={view} box={b} src={src} style={{ aspectRatio: `${w} / ${h}`, [w >= h ? "width" : "height"]: "100%" }} />
      <span
        className="absolute bottom-0.5 left-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 bg-white text-[10px] font-bold text-slate-900 shadow"
        style={{ borderColor: colorOf(p) }}
      >
        {p.n}
      </span>
    </button>
  );
}

export function PartRow({ part: p, active, onClick, onHover, rowRef, thumb, onZoom }) {
  return (
    <li
      ref={rowRef}
      onClick={onClick}
      onMouseEnter={() => onHover?.(p.n)}
      onMouseLeave={() => onHover?.(null)}
      className={`flex cursor-pointer gap-3 rounded-xl p-2.5 transition-colors ${
        active ? "bg-sky-800/50 ring-1 ring-sky-400/50" : "hover:bg-sky-900/40"
      }`}
    >
      {thumb && p.box ? (
        <PartThumb {...thumb} part={p} onZoom={onZoom} />
      ) : (
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-[3px] bg-white text-xs font-bold text-slate-900"
          style={{ borderColor: colorOf(p) }}
        >
          {p.n}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <span className="text-sm font-semibold text-white">{p.name}</span>
          {p.codes?.[0] && (
            <span
              title={p.codes[0].title}
              className="shrink-0 rounded-md bg-sky-950/80 px-1.5 py-0.5 font-mono text-[11px] text-sky-300 ring-1 ring-sky-800/60"
            >
              {fmtCode(p.codes[0].code)}
            </span>
          )}
        </div>
        <div className="text-xs text-sky-100/70">
          {p.material}
          {!p.visible_in_photo && <span className="text-amber-300/90"> · taxminiy</span>}
        </div>
        <div className="text-xs text-sky-200/50">{p.function}</div>
      </div>
    </li>
  );
}

/** Jarayon kadri — umumiy rasmdan kesib ko'rsatiladi. */
export function Crop({ view, box, src, className = "", style }) {
  const [y0, x0, y1, x1] = box;
  const w = x1 - x0;
  const h = y1 - y0;
  return (
    <div
      className={`bg-white ${className}`}
      style={{
        backgroundImage: `url(${src ?? view.files.image})`,
        backgroundSize: `${100000 / w}% ${100000 / h}%`,
        backgroundPosition: `${w < 1000 ? (x0 / (1000 - w)) * 100 : 0}% ${h < 1000 ? (y0 / (1000 - h)) * 100 : 0}%`,
        ...style,
      }}
    />
  );
}

function Overlay({ title, onClose, children, actions = null, dark = false }) {
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", esc);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className={`fixed inset-0 z-50 flex flex-col font-sans text-slate-100 ${dark ? "bg-black" : "bg-[#04142b]"}`}
    >
      <header className="flex shrink-0 items-center gap-3 border-b border-sky-900/50 px-4 py-3">
        <h2 className="min-w-0 flex-1 truncate text-base font-semibold text-white">{title}</h2>
        {actions}
        <button
          onClick={onClose}
          title="Yopish"
          className="rounded-full bg-sky-900/60 p-2 text-white transition-colors hover:bg-sky-800"
        >
          <X className="h-5 w-5" />
        </button>
      </header>
      {children}
    </div>,
    document.body,
  );
}

/** Qismlar: rasm qotib turadi, ro'yxat suriladi; raqam bosilsa ro'yxat o'sha qismga boradi. */
export function PartsViewer({ view, size, initial, src, onClose }) {
  const close = useBackClose(onClose);
  const frames = partFrames(view.parts);
  const [zoom, setZoom] = useState(null);
  const closeZoom = useCallback(() => setZoom(null), []);
  const [active, setActive] = useState(initial);
  const [hover, setHover] = useState(null);
  const rows = useRef({});
  const wide = useWide();

  useEffect(() => {
    if (active) rows.current[active]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [active]);

  return (
    <Overlay title={view.product?.name} onClose={close}>
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {/* Telefonda rasm balandligi o'ziga mos (ostida bo'sh joy qolmasin), lekin ekranning yarmidan oshmaydi */}
        <div className={wide ? "min-h-0 min-w-0 flex-1 overflow-hidden p-6" : "shrink-0 p-3"}>
          <Exploded
            view={view}
            size={size}
            active={hover ?? active}
            onPick={setActive}
            onHover={setHover}
            src={src}
            fill={wide}
            maxH={wide ? undefined : window.innerHeight * 0.5}
            className="rounded-2xl"
          />
        </div>

        <ol className="min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain border-t border-sky-900/50 p-3 lg:w-[420px] lg:flex-none lg:border-l lg:border-t-0">
          {(view.parts ?? []).map((p) => (
            <PartRow
              key={p.n}
              part={p}
              active={active === p.n}
              onClick={() => setActive(active === p.n ? null : p.n)}
              onHover={setHover}
              rowRef={(el) => (rows.current[p.n] = el)}
              thumb={{ view, size, src }}
              onZoom={(n) => setZoom(frames.findIndex((f) => f.key === n))}
            />
          ))}
        </ol>
      </div>
      {zoom !== null && zoom >= 0 && (
        <FrameViewer view={view} size={size} items={frames} index={zoom} src={src} onClose={closeZoom} />
      )}
    </Overlay>
  );
}

/** Kadrlar (qismlar yoki jarayon): kattalashtirilgan, oldinga/orqaga (strelka, surish, ekran yarmi). */
export function FrameViewer({ view, size, items: steps, index, src, onClose }) {
  const close = useBackClose(onClose);
  const [i, setI] = useState(index);
  const touch = useRef(null);
  const step = steps[i];
  const [y0, x0, y1, x1] = step.box;
  // Kadr umumiy rasmdan kesilgan (~300 px) — 3 barobardan ortiq kattalashsa xira
  const [fitRef, fit] = useFit(((x1 - x0) / 1000) * size.w, ((y1 - y0) / 1000) * size.h, 3);

  const go = useCallback((d) => setI((x) => (x + d + steps.length) % steps.length), [steps.length]);

  useEffect(() => {
    const key = (e) => {
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [go]);

  const arrow =
    "pointer-events-none absolute top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white/80 sm:p-3";

  return (
    <Overlay title={`${i + 1} / ${steps.length}`} onClose={close} dark>
      {/* Chap yarmini bosish — oldingi, o'ng yarmini — keyingi (surish ham ishlaydi) */}
      <div
        className="relative min-h-0 flex-1 cursor-pointer select-none p-4 sm:px-20"
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          go(e.clientX - r.left < r.width / 2 ? -1 : 1);
        }}
        onTouchStart={(e) => (touch.current = e.touches[0].clientX)}
        onTouchEnd={(e) => {
          const dx = e.changedTouches[0].clientX - (touch.current ?? 0);
          if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
        }}
      >
        <div ref={fitRef} className="flex h-full w-full items-center justify-center">
          {fit && <Crop view={view} box={step.box} src={src} className="rounded-xl" style={fit} />}
        </div>
        {steps.length > 1 && (
          <>
            <span className={`${arrow} left-2 sm:left-4`}>
              <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
            </span>
            <span className={`${arrow} right-2 sm:right-4`}>
              <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
            </span>
          </>
        )}
      </div>

      <div className="shrink-0 space-y-3 px-4 pb-6 pt-2 text-center">
        <div>
          <p className="text-lg font-semibold text-white">{step.title}</p>
          {step.sub && <p className="mt-0.5 text-sm text-sky-200/70">{step.sub}</p>}
        </div>
        <div className="flex justify-center gap-1.5">
          {steps.map((s, j) => (
            <button
              key={s.key}
              onClick={() => setI(j)}
              aria-label={s.title}
              className={`h-2 rounded-full transition-all ${j === i ? "w-6 bg-sky-400" : "w-2 bg-white/30"}`}
            />
          ))}
        </div>
      </div>
    </Overlay>
  );
}

/** Poster: ekranga sig'adi; bosilgan joyi kattalashadi (to'liq o'lchamdagi nusxa), yana bosilsa qaytadi. */
export function PosterViewer({ title, src, fullSrc, placeholder, onClose, onDownload, busy }) {
  const close = useBackClose(onClose);
  const [ready, setReady] = useState(false);
  const [zoom, setZoom] = useState(0); // 0 — sig'dirilgan, aks holda eni px
  const box = useRef(null);
  const focus = useRef([0.5, 0.5]);

  const toggle = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    focus.current = [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height];
    setZoom((z) => (z ? 0 : Math.max(box.current.clientWidth * 2.5, 1600)));
  };

  useLayoutEffect(() => {
    const el = box.current;
    if (!zoom || !el) return;
    const [fx, fy] = focus.current;
    const h = (zoom * el.querySelector("img").naturalHeight) / el.querySelector("img").naturalWidth;
    el.scrollLeft = fx * zoom - el.clientWidth / 2;
    el.scrollTop = fy * h - el.clientHeight / 2;
  }, [zoom]);

  return (
    <Overlay
      title={title}
      onClose={close}
      actions={
        <button
          onClick={onDownload}
          disabled={busy}
          title="Yuklab olish"
          className="flex items-center gap-2 rounded-full bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-500 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          <span className="hidden sm:inline">Yuklab olish</span>
        </button>
      }
    >
      <div ref={box} className="relative min-h-0 flex-1 overflow-auto overscroll-contain">
        <div className={zoom ? "p-2" : "flex min-h-full items-center justify-center p-2 sm:p-6"}>
          {/* Kartadagi kichik nusxa (keshda) — darhol, xira; aniq nusxa kelgach almashadi */}
          {!ready &&
            (placeholder ? (
              <img src={placeholder} alt="" className="w-full max-w-6xl rounded-lg bg-white opacity-80 blur-sm" />
            ) : (
              <div className="skeleton aspect-[4/3] w-full max-w-6xl rounded-lg" />
            ))}
          <img
            src={zoom ? fullSrc : src}
            alt={title}
            onLoad={() => setReady(true)}
            onClick={toggle}
            style={zoom ? { width: zoom, maxWidth: "none" } : undefined}
            className={`rounded-lg bg-white ${zoom ? "cursor-zoom-out" : "w-full max-w-6xl cursor-zoom-in"} ${
              ready ? "fade-in" : "hidden"
            }`}
          />
        </div>
      </div>
      {ready && (
        <button
          onClick={(e) => {
            focus.current = [0.5, 0.5];
            setZoom((z) => (z ? 0 : Math.max(box.current.clientWidth * 2.5, 1600)));
            e.stopPropagation();
          }}
          title={zoom ? "Kichraytirish" : "Kattalashtirish"}
          className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 rounded-full bg-slate-900/80 p-3 text-white shadow-lg ring-1 ring-white/20"
        >
          {zoom ? <ZoomOut className="h-5 w-5" /> : <ZoomIn className="h-5 w-5" />}
        </button>
      )}
    </Overlay>
  );
}

/** Foydalanuvchi yuborgan foto(lar): ekranga sig'adi, bosilgan joyi kattalashadi; bir nechtasi — o'q yoki surish. */
export function PhotoViewer({ photos, thumbs = [], index = 0, onClose }) {
  const close = useBackClose(onClose);
  const [i, setI] = useState(index);
  const [ready, setReady] = useState(false);
  const [zoom, setZoom] = useState(0); // 0 — sig'dirilgan, aks holda eni px
  const box = useRef(null);
  const focus = useRef([0.5, 0.5]);
  const touch = useRef(null);
  const n = photos.length;

  const go = useCallback((d) => {
    setZoom(0);
    setReady(false);
    setI((x) => (x + d + n) % n);
  }, [n]);

  useEffect(() => {
    if (n < 2) return undefined;
    const key = (e) => {
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [go, n]);

  const zoomTo = (fx, fy) => {
    focus.current = [fx, fy];
    const img = box.current.querySelector("img");
    setZoom((z) => (z ? 0 : Math.max(box.current.clientWidth * 2, Math.min(img.naturalWidth, 2400))));
  };

  useLayoutEffect(() => {
    const el = box.current;
    if (!zoom || !el) return;
    const img = el.querySelector("img");
    const [fx, fy] = focus.current;
    el.scrollLeft = fx * zoom - el.clientWidth / 2;
    el.scrollTop = fy * ((zoom * img.naturalHeight) / img.naturalWidth) - el.clientHeight / 2;
  }, [zoom]);

  const arrow = "absolute top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white/80 hover:bg-white/20 sm:p-3";

  return (
    <Overlay title={n > 1 ? `Siz yuborgan foto · ${i + 1} / ${n}` : "Siz yuborgan foto"} onClose={close} dark>
      <div className="relative min-h-0 flex-1">
        <div
          ref={box}
          className="h-full overflow-auto overscroll-contain"
          onTouchStart={(e) => (touch.current = e.touches[0].clientX)}
          onTouchEnd={(e) => {
            const dx = e.changedTouches[0].clientX - (touch.current ?? 0);
            if (!zoom && n > 1 && Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
          }}
        >
          <div className={zoom ? "p-2" : "flex h-full items-center justify-center p-2 sm:px-20 sm:py-6"}>
            {!ready &&
              (thumbs[i] ? (
                <img src={thumbs[i]} alt="" className="max-h-full max-w-full rounded-lg object-contain opacity-80 blur-sm" />
              ) : (
                <div className="skeleton aspect-[4/3] w-full max-w-3xl rounded-lg" />
              ))}
            <img
              key={photos[i]}
              src={photos[i]}
              alt=""
              onLoad={() => setReady(true)}
              onClick={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                zoomTo((e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height);
              }}
              style={zoom ? { width: zoom, maxWidth: "none" } : undefined}
              className={`rounded-lg ${zoom ? "cursor-zoom-out" : "max-h-full max-w-full cursor-zoom-in object-contain"} ${
                ready ? "fade-in" : "hidden"
              }`}
            />
          </div>
        </div>
        {n > 1 && !zoom && (
          <>
            <button onClick={() => go(-1)} aria-label="Oldingi foto" className={`${arrow} left-2 sm:left-4`}>
              <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
            <button onClick={() => go(1)} aria-label="Keyingi foto" className={`${arrow} right-2 sm:right-4`}>
              <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </>
        )}
        {ready && (
          <button
            onClick={() => zoomTo(0.5, 0.5)}
            title={zoom ? "Kichraytirish" : "Kattalashtirish"}
            className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2 rounded-full bg-slate-900/80 p-3 text-white shadow-lg ring-1 ring-white/20"
          >
            {zoom ? <ZoomOut className="h-5 w-5" /> : <ZoomIn className="h-5 w-5" />}
          </button>
        )}
      </div>
      {n > 1 && (
        <div className="flex shrink-0 justify-center gap-1.5 pb-6 pt-3">
          {photos.map((src, j) => (
            <button
              key={src}
              onClick={() => j !== i && go(j - i)}
              aria-label={`${j + 1}-foto`}
              className={`h-2 rounded-full transition-all ${j === i ? "w-6 bg-sky-400" : "w-2 bg-white/30"}`}
            />
          ))}
        </div>
      )}
    </Overlay>
  );
}
